import { useState, ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Home, MapPin, Car, Info, Mail, Phone } from "lucide-react";

interface Props {
  trigger: ReactNode;
}

const eastCities = [
  "Balma",
  "Pin-Balma",
  "Quint-Fonsegrives",
  "Flourens",
  "Drémil-Lafage",
  "Aigrefeuille",
  "Saint-Orens-de-Gameville",
];

const southEastCities = [
  "Ramonville-Saint-Agne",
  "Auzeville-Tolosane",
  "Labège",
  "Castanet-Tolosan",
  "Auzielle",
  "Lauzerville",
  "Escalquens",
  "Belberaud",
  "Odars",
  "Fourquevaux",
  "Péchabou",
  "Pompertuzat",
  "Deyme",
  "Donneville",
  "Montlaur",
  "Montgiscard",
  "Labastide-Beauvoir",
  "Baziège",
  "Montbrun-Lauragais",
  "Belbèze-de-Lauragais",
  "Ayguesvives",
  "Noueilles",
  "Issus",
  "Pouze",
  "Villenouvelle",
  "Vieillevigne",
  "Montesquieu-Lauragais",
  "Gardouch",
  "Renneville",
  "Saint-Rome",
  "Montgaillard-Lauragais",
  "Villefranche-de-Lauragais",
];

const allowedAccompaniments = [
  "Bilan naturopathique – Enfants & Adolescents",
  "Bilan naturopathique – Adultes",
  "PARCOURS FAMILLE COURAGEUSE",
];

export const HomeVisitDialog: React.FC<Props> = ({ trigger }) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Home className="w-5 h-5 text-primary" />
            </div>
            <DialogTitle className="font-cinzel text-primary text-xl">
              Consultations à domicile
            </DialogTitle>
          </div>
          <DialogDescription className="font-montserrat text-muted-foreground">
            Je me déplace à votre domicile selon les secteurs indiqués ci-dessous.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-6 font-montserrat text-foreground">
          {/* Zones */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border/60 p-4 bg-muted/20">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-secondary" />
                <h4 className="font-semibold text-primary">Toulouse 31500 – secteur Est</h4>
              </div>
              <ul className="text-sm space-y-1 text-foreground/80">
                {eastCities.slice(0, 7).map((city) => (
                  <li key={city}>{city}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-border/60 p-4 bg-muted/20">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-secondary" />
                <h4 className="font-semibold text-primary">Toulouse 31400 – secteur Sud / Sud-Est</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Est toulousain & Lauragais</p>
              <ul className="text-sm space-y-1 text-foreground/80 max-h-[180px] overflow-y-auto pr-1">
                {southEastCities.map((city) => (
                  <li key={city}>{city}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Accompagnements concernés */}
          <div className="rounded-lg border border-secondary/30 bg-secondary/5 p-4">
            <h4 className="font-semibold text-primary mb-3">
              Accompagnements proposés à domicile
            </h4>
            <p className="text-sm text-foreground/80 mb-3">
              Les consultations à domicile sont uniquement proposées pour les bilans suivants&nbsp;:
            </p>
            <ul className="space-y-2">
              {allowedAccompaniments.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm">
                  <span className="text-secondary mt-0.5">✔</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Déplacement offert */}
          <div className="flex items-start gap-3 rounded-lg border border-border/60 p-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Car className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-1">Déplacement à domicile offert</h4>
              <p className="text-sm text-foreground/80 leading-relaxed">
                Les consultations à domicile sont actuellement proposées sans frais de déplacement sur Toulouse Est, Sud-Est et sur le Lauragais jusqu'à Villefranche-de-Lauragais. La zone de déplacement peut évoluer selon les disponibilités.
              </p>
            </div>
          </div>

          {/* Note hors zone */}
          <div className="flex items-start gap-3 rounded-lg border border-border/60 p-4 bg-muted/30">
            <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground/80 leading-relaxed">
              Votre adresse se situe en dehors de ma zone habituelle de déplacement. Vous pouvez néanmoins me contacter afin que nous étudions ensemble la possibilité d'un rendez-vous.
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              asChild
              variant="outline"
              className="font-montserrat border-primary/40 text-primary hover:bg-primary/10"
            >
              <a href="mailto:contact@regard-de-nature.fr">
                <Mail className="w-4 h-4 mr-2" />
                Envoyer un email
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="font-montserrat border-primary/40 text-primary hover:bg-primary/10"
            >
              <a href="sms:+33637031866">
                <Phone className="w-4 h-4 mr-2" />
                Envoyer un SMS
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
